import { Button, Col, Input, Modal, Row, Tooltip } from 'antd';
import classNames from 'classnames';
import { ChangeEvent, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { WarningOutlined } from '@ant-design/icons';
import { SMALL_JOB_AREA } from '@common/constants';
import { useLazyFetchTicketQuery } from '@ee-api/ticket-validation-api/ticketValidationService';

import './ConfirmSmallProject.scss';

interface ConfirmSmallProjectProps {
    value?: string;
    isVisible?: boolean;
    minSqFt?: number;
    onCancel?: () => void;
    onContinue: () => void;
    onSuccess: (ticketNumber: string) => void;
    onChange?: (ticketNumber: string) => void;
}

/**
 *
 * @param onCancel ()=>void action to take on cancel button click
 * @param onContinue ()=>void action to take on "Continue with no code" button
 * @param onSuccess (ticketNumber)=>void action to take after verifying the ticket number
 * @param onChange (value)=>void action taken on changing the value
 * @param minSqFt minimum square feet used for this material
 * @param isVisible
 * @param value initial value of the ticket number - should only ever persist if it was valid
 */
const ConfirmSmallProject = ({
    onCancel,
    onContinue,
    onSuccess,
    onChange,
    minSqFt,
    isVisible,
    value,
}: ConfirmSmallProjectProps): JSX.Element => {
    const { t } = useTranslation();

    const [validateTicketQuery] = useLazyFetchTicketQuery();
    const [tmpTicketNumber, setTmpTicketNumber] = useState('');
    const [shouldShowError, setShouldShowError] = useState(false);
    const [isValidating, setIsValidating] = useState(false);

    const codeMinLength = 8;
    const codeMaxLength = 8;

    // show the most verified ticket number if available
    useEffect(() => {
        setTmpTicketNumber(isVisible && !!value ? value : '');
    }, [value, isVisible]);

    const onTicketNumberChange = useCallback(
        (e: ChangeEvent<HTMLInputElement>) => {
            setShouldShowError(false);
            const cleanTicketNumber = e.target.value.replace(/[\W_]/g, '').toUpperCase();
            setTmpTicketNumber(cleanTicketNumber);
            if (onChange) {
                onChange(cleanTicketNumber);
            }
        },
        [onChange]
    );

    const onSmallProjectVerify = async (ticketNumber: string) => {
        setIsValidating(true);
        const result = await validateTicketQuery({ ticketNumber }, false);
        setIsValidating(false);
        const isValidTicket = result.isSuccess && result.data?.ticketNumber === ticketNumber;
        setShouldShowError(!isValidTicket);
        if (isValidTicket) {
            onSuccess(ticketNumber); // success, return the valid result
        }
    };

    const isTicketVerifiable = useCallback((ticketNumber: string): boolean => {
        return ticketNumber.length >= codeMinLength && ticketNumber.length <= codeMaxLength;
    }, []);

    return (
        <Modal
            centered
            destroyOnClose
            open={isVisible}
            closable={false}
            maskClosable
            footer={null}
            wrapProps={{ span: 24 }}
            className="ConfirmSmallProject"
        >
            <div className="ConfirmSmallProject__title">{t('SMALL_PROJECTS.TITLE')}</div>
            <div className="ConfirmSmallProject__body">
                {t('SMALL_PROJECTS.NOTIFICATION', { minSqFt: minSqFt ?? SMALL_JOB_AREA.DEFAULT_HIGH })}
            </div>
            <div className="ConfirmSmallProject__content-container">
                <div className="ConfirmSmallProject__input-container">
                    <Input
                        key="ticketNumber"
                        maxLength={codeMaxLength}
                        minLength={codeMinLength}
                        allowClear
                        value={tmpTicketNumber}
                        disabled={isValidating}
                        placeholder="Permission Code"
                        status={shouldShowError ? 'error' : undefined}
                        addonBefore={
                            shouldShowError ? (
                                <Tooltip title={t('SMALL_PROJECTS.ERROR.INVALID')}>
                                    <WarningOutlined />
                                </Tooltip>
                            ) : null
                        }
                        addonAfter={
                            <Button
                                key="validateButton"
                                disabled={isValidating || !isTicketVerifiable(tmpTicketNumber)}
                                className="ConfirmSmallProject__search-button"
                                loading={isValidating}
                                onClick={() => {
                                    onSmallProjectVerify(tmpTicketNumber);
                                }}
                            >
                                {t('SMALL_PROJECTS.BUTTONS.VERIFY')}
                            </Button>
                        }
                        size="large"
                        onChange={onTicketNumberChange}
                        className={classNames('ConfirmSmallProject__search')}
                    />
                    {shouldShowError && (
                        <span className="ConfirmSmallProject__error-message">{t('SMALL_PROJECTS.ERROR.INVALID')}</span>
                    )}
                </div>
                <Row justify="end" gutter={8}>
                    <Col>
                        <Button key="continueButton" className="ConfirmSmallProject__button" onClick={onContinue}>
                            {t('SMALL_PROJECTS.BUTTONS.CONTINUE')}
                        </Button>
                    </Col>
                    <Col>
                        <Button key="cancelButton" className="ConfirmSmallProject__button" onClick={onCancel}>
                            {t('SMALL_PROJECTS.BUTTONS.CANCEL')}
                        </Button>
                    </Col>
                </Row>
            </div>
        </Modal>
    );
};
export default ConfirmSmallProject;
